import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { ContractSignatory } from "@academy-context/shared/domain/types/contract-signatory";
import type { DateInterval } from "@shared-kernel/core/types/date-interval";
import type { TrainingLocation } from "@shared-kernel/domain/types/postal-address";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import type { ApiTrainingSchedule } from "@shared-kernel/core/types/api-training-schedule";
import type { EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";
import type { ATTACHMENT_LABEL } from "@academy-context/shared/external-provider-batch/types/provider-training-form.types";
import type {
  ObjectiveDetailVM,
  TrainingItemAppraisalVM,
  TrainingItemEvaluationPayload,
} from "@academy-context/read/domain/types/shared/training-item-evaluation";
import type { ORGANIZATION } from "@shared-kernel/domain/organization";
import type { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";

export enum SUBCONTRACTING_CONTRACT_STATUS {
  PENDING = "pending",
  SIGNED = "signed",
}

export interface SubcontractingContract {
  // Download endpoint is only available once the contract is signed
  downloadEndpoint: Nullable<string>;
  name: string;
  status: SUBCONTRACTING_CONTRACT_STATUS;
  // When the contract was activated for signing
  activatedAt: Nullable<string>;
  signedAt: Nullable<string>;
  // Null on a contract issued before the signatory was chosen
  signatory: Nullable<ContractSignatory>;
}

export interface InitializeContractBody {
  signatory: ContractSignatory;
}

// ========================================
// View Model Types (Read)
// ========================================

/** Speaker type for multiple speakers */
export interface Speaker {
  firstName: string;
  lastName: string;
  resumeUrl: Nullable<string>;
}

/** Invoice status */
export type ExternalProviderInvoiceStatus = "to_review" | "approved" | "rejected" | "paid";

/** Dashboard response from GET /v1/external-provider/dashboard */
export interface ExternalProviderDashboardVM {
  status: EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS;

  provider: {
    name: string;
    contact: {
      firstName: string;
      lastName: string;
    };
    legalRepresentative: Nullable<{
      firstName: string;
      lastName: string;
    }>;
    email: string;
    phone: string;
    siret: Nullable<string>;
    country: Nullable<SUPPORTED_COUNTRY>;
    activityRegistrationNumber: Nullable<string>;
    iban: Nullable<string>;
  };

  student: {
    firstName: string;
    lastName: string;
  };

  training: {
    title: string;
    description: string;
    objective: string;
    price: number;
    trainingSchedules: ApiTrainingSchedule[];
    interval: DateInterval;
    attachments: { url: string; label: ATTACHMENT_LABEL }[];
    websiteUrl: Nullable<string>;
    trainingLocation: Nullable<TrainingLocation>;
    equipmentDescription: Nullable<string>;
    speakers: Speaker[];
  };

  request: {
    id: string;
    organization: Nullable<ORGANIZATION>;
    subcontractingContract: Nullable<SubcontractingContract>;
  };

  participation: Nullable<{
    id: string;
  }>;

  objectives: ObjectiveDetailVM[];

  appraisal: Nullable<TrainingItemAppraisalVM>;

  invoice: Nullable<{
    id: string;
    invoiceNumber: string;
    invoiceUrl: string;
    amount: number;
    adminNote: Nullable<string>;
    submittedAt: string;
    invoiceDate: string;
    invoiceDeadline: string;
  }>;
}

// ========================================
// Command Body Types (Write)
// ========================================

/**
 * Submission body for POST /v1/external-provider/participations/{participationId}:evaluate
 *
 * Un acte de groupe : tous les objectifs de la participation notés d'un coup, plus l'appréciation
 * qui porte sur la formation menée. Le backend rejette un envoi partiel.
 */
export type SubmitEvaluationBody = TrainingItemEvaluationPayload;

/** Submission body for POST /v1/external-provider/participations/{participationId}:submit-invoice */
export interface SubmitInvoiceBody {
  invoiceNumber: string;
  invoiceUrl: string;
  price: number;
  providerIban: string;
  invoiceDate: string;
  invoiceDeadline: string;
}

/** Submission body for POST /v1/external-provider/requests/{requestId} */
export interface SubmitRequestDataBody {
  // Provider fields (can be corrected by provider)
  providerName: string;
  providerContactFirstName: string;
  providerContactLastName: string;
  providerEmail: string;
  providerPhone: string;
  providerSiret: string;
  providerCountry: SUPPORTED_COUNTRY;
  providerActivityRegistrationNumber: Nullable<string>;
  // Training fields (can be corrected by provider)
  title: string;
  description: string;
  objective: string;
  price: number;
  trainingSchedules: ApiTrainingSchedule[];
  attachments: { url: string; label: ATTACHMENT_LABEL }[];
  websiteUrl: string | null;
  // Pure provider fields
  legalRepresentativeFirstName: string;
  legalRepresentativeLastName: string;
  trainingLocation: AddressLookup;
  trainingAddressComplement: Nullable<string>;
  equipmentDescription: string;
  speakers: Speaker[];
}

// ========================================
// Repository Interface (Port)
// ========================================

export interface ExternalProviderRepository {
  // Read Operations
  getDashboard(): Promise<ExternalProviderDashboardVM>;

  // Write Operations
  submitEvaluation(participationId: string, body: SubmitEvaluationBody): Promise<void>;
  uploadInvoice(file: File): Promise<string>;
  submitInvoice(participationId: string, body: SubmitInvoiceBody): Promise<void>;
  uploadSpeakerResume(file: File): Promise<string>;
  submitRequestData(requestId: string, body: SubmitRequestDataBody): Promise<void>;
  initializeContract(body: InitializeContractBody): Promise<string>;
}
